import { useTranslation } from "@repo/shared/i18n";
import {
  formatExpiryAt,
  formatTime,
  getTimeBreakdown,
} from "@repo/shared/utils";

import {
  expire as expireIcon,
  noExpiring as noExpiringIcon,
  nonRepeatable as nonRepeatableIcon,
  repeatable as repeatableIcon,
  repeatableInterval as repeatableIntervalIcon,
} from "../../../../icons";

type Props = {
  repeatable: boolean;
  repeats?: number;
  refreshInterval?: number;
  startTime: number;
  endTime?: number;
};

/**
 * How long the set lasts and how often it comes back. Meta about the set
 * rather than the set itself, so the whole line sits at one muted weight,
 * icons included, and wraps rather than running off the side.
 */
export const SetHeaderDetails = ({
  repeatable,
  repeats,
  refreshInterval,
  startTime,
  endTime,
}: Props) => {
  const { t } = useTranslation("sbcs");
  /* Derived here rather than taken from the set: only the list request works
     one out, so a set opened directly had no expiry to show. */
  const timeRemaining = formatExpiryAt(endTime);
  const currentTimestamp = Math.floor(Date.now() / 1000);
  const timeElapsed = currentTimestamp - startTime;
  const timeUntilRefresh = refreshInterval - (timeElapsed % refreshInterval);
  const refreshMs = refreshInterval * 1000;
  const { days: refreshDays, hours: refreshHours } =
    getTimeBreakdown(refreshMs);
  const refreshIntervalLabel =
    refreshDays === 7
      ? t("setHeader.refreshInterval.weekly")
      : refreshHours === 24
        ? t("setHeader.refreshInterval.daily")
        : t("setHeader.refreshInterval.every", { time: formatTime(refreshMs) });

  return (
    <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-foreground-muted">
      <span className="flex items-center gap-x-1">
        <span className="size-4 shrink-0">
          {repeatable ? repeatableIcon : nonRepeatableIcon}
        </span>
        {repeatable
          ? repeats > 0
            ? t("set.repeatableWithCount", { count: repeats })
            : t("set.repeatable")
          : t("set.nonRepeatable")}
      </span>

      {repeatable && repeats > 0 && timeUntilRefresh > 0 && (
        <span className="flex items-center gap-x-1">
          <span className="size-4 shrink-0">{repeatableIntervalIcon}</span>
          {t("setHeader.refreshesIn", {
            time: formatTime(timeUntilRefresh * 1000),
          })}
          <span className="text-foreground-faint">
            ({refreshIntervalLabel})
          </span>
        </span>
      )}

      {/* Far-out end dates are placeholders for "never", so they are hidden. */}
      {(timeRemaining || !endTime) && (
        <span className="flex items-center gap-x-1">
          <span className="size-4 shrink-0">
            {timeRemaining ? expireIcon : noExpiringIcon}
          </span>
          {timeRemaining
            ? t("setHeader.expiresIn", { time: timeRemaining })
            : t("set.noExpiry")}
        </span>
      )}
    </div>
  );
};
